import React, { useEffect, useState } from 'react';
import { formatDate, formatCurrency, formatPercentage } from '../../utils/formatters';
import { calculoService, CalculoData, CalculoListParams, getClienteNome, getClienteEmail, getClienteTelefone, getClienteCpfCnpj, getClienteTipoPessoa } from '../../services/calculoService';

interface Props {
  onEdit: (c: CalculoData) => void;
  onView?: (c: CalculoData) => void;
  onRefresh: () => void;
  onDelete?: (c: CalculoData) => void;
  activeId?: string | null;
  onRegisterNavigator?: (fn: (currentId?: string) => Promise<CalculoData | undefined>) => void;
  refreshTrigger?: number;
}

export const CotacaoTable: React.FC<Props> = ({ 
  onEdit, 
  onView, 
  onRefresh, 
  onDelete, 
  onRegisterNavigator, 
  activeId, 
  refreshTrigger 
}) => {
  const [items, setItems] = useState<CalculoData[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>('dataInclusa');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterProduto, setFilterProduto] = useState<string>('');
  const [filterSeguradora, setFilterSeguradora] = useState<string>('');
  const [filterSituacaoId, setFilterSituacaoId] = useState<string>('');
  const [filterCpfCnpj, setFilterCpfCnpj] = useState<string>('');

  const loadCalculos = async (params?: { page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
    setLoading(true);
    try {
      const queryParams: CalculoListParams = {
        search: params?.search || searchTerm || undefined,
        page: params?.page || page,
        limit: params?.limit || limit,
        sortBy: params?.sortBy || sortBy,
        sortOrder: params?.sortOrder || sortOrder,
        status: filterStatus || undefined,
        produto: filterProduto || undefined,
        seguradora: filterSeguradora || undefined,
        situacaoId: filterSituacaoId || undefined,
        cpfCnpj: filterCpfCnpj || undefined,
      };
      
      const response = await calculoService.list(queryParams);
      
      setItems(response.data || []);
      setTotalPages(response.pagination?.totalPages);
    } catch (e) {
      console.error('Erro ao carregar cálculos:', e);
      // Em caso de erro, usar dados mockados para desenvolvimento
      const mockData: CalculoData[] = [
        { 
          id: '1', 
          calculo: '2869326',
          cpfCnpj: '33997030820',
          nomeCliente: 'João Silva',
          produto: 'MULTICÁLCULO',
          situacao: 'Em análise',
          perdido: false,
          zerokm: false,
          valor: 1200.50, 
          percentualComis: 20.00,
          valorComis: 240.10,
          inicioVigencia: '2026-01-01',
          dataInclusa: '2026-01-01T10:00:00.000Z',
          item: 'CRUZE SEDAN LT AT 1.4 16v',
          tipoPessoa: 'Física',
          email: 'joao@email.com',
          celular: '999887766',
          dddCelular: '11',
          usuarioInclusao: 'Sistema',
          nivel: 'FRANQUIA',
          divisao: 'SÃO PAULO',
          empresaId: 'empresa-1',
          criadoEm: '2026-01-01T10:00:00.000Z',
          atualizadoEm: '2026-01-01T10:00:00.000Z'
        },
        { 
          id: '2', 
          calculo: '2869327',
          cpfCnpj: '12345678901',
          nomeCliente: 'Maria Santos',
          produto: 'RESIDENCIAL',
          situacao: 'Negócios ganhos',
          perdido: false,
          zerokm: true,
          valor: 850.00, 
          percentualComis: 18.00,
          valorComis: 153.00,
          inicioVigencia: '2026-01-02',
          dataInclusa: '2026-01-02T14:30:00.000Z',
          item: 'Casa',
          tipoPessoa: 'Física',
          email: 'maria@email.com',
          usuarioInclusao: 'Sistema',
          nivel: 'FRANQUIA',
          divisao: 'RIO DE JANEIRO',
          empresaId: 'empresa-1',
          criadoEm: '2026-01-02T14:30:00.000Z',
          atualizadoEm: '2026-01-02T14:30:00.000Z'
        },
      ];
      setItems(mockData);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalculos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadCalculos({ page, limit, search: searchTerm, sortBy, sortOrder });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Reload when filters change
  useEffect(() => {
    setPage(1);
    loadCalculos({ page: 1, limit, search: searchTerm, sortBy, sortOrder });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterProduto, filterSeguradora, filterSituacaoId, filterCpfCnpj]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadCalculos({ page: 1, limit, search: searchTerm, sortBy, sortOrder });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadCalculos({ page: newPage, limit, search: searchTerm, sortBy, sortOrder });
  };

  const handleSort = (field: string) => {
    if (field === sortBy) {
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
      setPage(1);
      loadCalculos({ page: 1, limit, search: searchTerm, sortBy: field, sortOrder: newOrder });
    } else {
      setSortBy(field);
      setSortOrder('asc');
      setPage(1);
      loadCalculos({ page: 1, limit, search: searchTerm, sortBy: field, sortOrder: 'asc' });
    }
  };

  if (loading) return <div className="p-3 text-center text-xs">Carregando cotações...</div>;

  return (
    <div className="space-y-3">
      {/* Header e Busca */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xs font-medium" style={{ color: '#3d2b56' }}>
          Cálculos ({items.length})
        </h2>
        
        <div className="flex gap-1 flex-wrap">
          {/* Campo de ordenação */}
          <div className="flex items-center gap-1">
            <label className="text-xs" style={{ color: '#3d2b56' }}>Ordenar por:</label>
            <select
              value={sortBy}
              onChange={(e) => handleSort(e.target.value)}
              className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212',
                minWidth: '140px'
              }}
            >
              <option value="dataInclusa">Data Inclusão</option>
              <option value="calculo">Nº Cálculo</option>
              <option value="nomeCliente">Cliente</option>
              <option value="valor">Valor</option>
              <option value="situacao">Situação</option>
              <option value="produto">Produto</option>
            </select>
            <button
              onClick={() => {
                const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                setSortOrder(newOrder);
                setPage(1);
                loadCalculos({ page: 1, limit, search: searchTerm, sortBy, sortOrder: newOrder });
              }}
              className="px-2 py-1 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 text-xs"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
              title={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          
          <form onSubmit={handleSearch} className="flex gap-1">
            <input
              type="text"
              placeholder="Buscar cálculos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212',
                minWidth: '160px'
              }}
            />
            <button
              type="submit"
              disabled={loading}
              className="px-2 py-1 text-xs font-medium text-white rounded-md hover:opacity-80 focus:outline-none focus:ring-1 disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: '#7A61B2' }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </form>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-6">
          <div className="text-xs" style={{ color: '#9E9E9E' }}>
            Carregando cálculos...
          </div>
        </div>
      )}

      {/* Cards Grid */}
      {!loading && items.length > 0 && (
        <>
          <div className="space-y-2">
            {items.map((item) => {
              // Normalizar situacao (pode vir como string ou objeto {id, nome})
              const situacaoTexto = typeof item.situacao === 'string' 
                ? item.situacao 
                : (item.situacao as any)?.nome || '-';
              const situacaoStr = situacaoTexto.toLowerCase();
              
              // Normalizar produto (pode vir como string ou objeto {id, nome})
              const produtoTexto = typeof item.produto === 'string'
                ? item.produto
                : (item.produto as any)?.nome || '-';
              
              // Determinar cor da borda baseada na situação
              let borderColor = '#10B981'; // Verde padrão para ganhos
              
              if (item.perdido || situacaoStr.includes('perdido')) {
                borderColor = '#EF4444'; // Vermelho para perdidos
              } else if (situacaoStr.includes('ganho')) {
                borderColor = '#10B981'; // Verde para ganhos
              } else if (situacaoStr.includes('análise')) {
                borderColor = '#3B82F6'; // Azul para em análise
              } else if (situacaoStr.includes('pendente')) {
                borderColor = '#F59E0B'; // Amarelo para pendente
              }
              
              return (
                <div 
                  key={item.id} 
                  data-cotacao-id={item.id} 
                  className={`bg-white rounded-lg shadow hover:shadow-md transition-shadow relative overflow-hidden ${activeId === item.id ? 'ring-2 ring-indigo-300' : ''}`}
                  style={{ borderLeft: `4px solid ${borderColor}` }}
                >
                  <div className="p-3">
                    {/* Header with Status and Actions */}
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          item.perdido || situacaoStr.includes('perdido') ? 'bg-red-100 text-red-800' :
                          situacaoStr.includes('ganho') ? 'bg-green-100 text-green-800' :
                          situacaoStr.includes('análise') ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {situacaoTexto}
                        </span>
                        {item.zerokm && (
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                            0 KM
                          </span>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex space-x-1">
                        <button 
                          onClick={() => onEdit(item)} 
                          className="text-green-600 hover:text-green-800 hover:bg-green-50 p-1 rounded transition-colors"
                          title="Visualizar/Editar"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {onDelete && (
                          <button 
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors" 
                            title="Excluir"
                            onClick={() => onDelete(item)}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                      {/* Left Column */}
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Nº Cálculo</div>
                            <div className="text-sm font-semibold text-gray-900 font-mono leading-tight">{item.calculo || '-'}</div>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Cliente</div>
                            <div className="text-sm font-semibold text-gray-900 leading-tight truncate">{getClienteNome(item)}</div>
                          </div>
                        </div>
                      </div>

                      {/* Middle Left Column */}
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Produto</div>
                            <div className="text-sm text-gray-700 leading-tight">{produtoTexto}</div>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Item</div>
                            <div className="text-sm text-gray-700 leading-tight truncate">{item.item || '-'}</div>
                          </div>
                        </div>
                      </div>

                      {/* Middle Right Column */}
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Data Inclusão</div>
                            <div className="text-sm text-gray-700 leading-tight">{item.dataInclusa ? formatDate(item.dataInclusa) : '-'}</div>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Valor</div>
                            <div className="text-sm font-semibold text-gray-900 leading-tight">{formatCurrency(item.valor)}</div>
                          </div>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="space-y-3">
                        <div>
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Comissão</span>
                          <p className="text-sm text-gray-700 leading-tight">{formatPercentage(item.percentualComis)}%</p>
                          <p className="text-sm font-semibold text-green-600 leading-tight">{formatCurrency(item.valorComis)}</p>
                        </div>
                        {item.email && (
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">E-mail</span>
                            <p className="text-xs text-gray-700 leading-tight truncate">{item.email}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Mensagem quando não há dados */}
      {!loading && items.length === 0 && (
        <div className="text-center py-8">
          <p className="text-xs" style={{ color: '#9E9E9E' }}>
            {searchTerm ? 'Nenhum cálculo encontrado para a busca.' : 'Nenhum cálculo cadastrado.'}
          </p>
        </div>
      )}

      {/* Paginação */}
      {!loading && items.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-xs" style={{ color: '#9E9E9E' }}>
            {totalPages ? `Página ${page} de ${totalPages}` : `Mostrando ${items.length} cálculos`}
          </div>
          
          <div className="flex space-x-1">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
            >
              Anterior
            </button>
            
            <span className="px-2 py-1 text-xs" style={{ color: '#3d2b56' }}>
              Página {page}{totalPages ? ` de ${totalPages}` : ''}
            </span>
            
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={totalPages ? page >= totalPages : items.length < limit}
              className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
